/**
 * Which of a booking's dates start costing money because they fall on a bank
 * holiday.
 *
 * The warning is for the case requesters actually miss: open days booked in bulk
 * over several months, every one of them free, with a bank holiday somewhere in
 * the run. So a date qualifies only when it would NORMALLY bill nothing and the
 * bank holiday makes it chargeable — worked out by running the same billing
 * rules twice, with the holidays and without, so the warning can never disagree
 * with the "Total Hours Billed" figure shown beside it.
 *
 * Guarding, 24/7 sites and Retail General visits bill identically either way, so
 * they never raise it.
 */

import { BilledHoursOptions, BilledShift, calculateBilledHours } from './billedHours';
import { BankHolidayMap } from './bankHolidays';
import { UsDateString, parseUsOrIso } from './date';

export interface BankHolidayCharge {
    date: UsDateString;
}

/** Held while the warning is up, so Continue can resume the navigation it interrupted. */
export interface BankHolidayModalState {
    open: boolean;
    navigationState: Record<string, unknown> | null;
}

export const bankHolidayChargeDates = (
    shifts: BilledShift[],
    options: Omit<BilledHoursOptions, 'bankHolidays'>,
    bankHolidays: BankHolidayMap,
): BankHolidayCharge[] => {
    const dates = Object.keys(bankHolidays);
    if (!dates.length) {
        return [];
    }

    const charged = new Map<UsDateString, BankHolidayCharge>();

    shifts.forEach((shift) => {
        if (charged.has(shift.date)) {
            return;
        }

        const normally = calculateBilledHours([shift], { ...options, bankHolidays: [] });
        const withHolidays = calculateBilledHours([shift], { ...options, bankHolidays: dates });

        if (normally === 0 && withHolidays > 0) {
            charged.set(shift.date, { date: shift.date });
        }
    });

    return Array.from(charged.values()).sort((a, b) => (
        parseUsOrIso(a.date).valueOf() - parseUsOrIso(b.date).valueOf()
    ));
};
